import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule} from '@angular/forms';
import {BaseCustomInputComponent} from '../base-custom-input.component';
import {CommonModule} from '@angular/common';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatMenuModule, MatMenuTrigger} from '@angular/material/menu';
import {MatIconModule} from '@angular/material/icon';
import {MatListModule} from '@angular/material/list';


@Component({
  selector: 'app-select-input',
  standalone: true,
  template: `
    <div class="row align-items-center no-gutter select-input" [ngClass]="{'mt-2': !noMargin, 'mb-2': bottomMargin}">
      <!-- Label Column -->
      <div *ngIf="label && !isIcon"
           [ngClass]="'col-' + labelColSize + ' pe-1'">
        {{ label }}
      </div>

      <!-- Spacer keeping an unlabeled select aligned with the labeled ones around it -->
      <div *ngIf="!label && reserveLabelSpace"
           aria-hidden="true"
           [ngClass]="'col-' + (isIcon ? iconLabelColSize : labelColSize) + ' pe-1'">
      </div>

      <!-- Icon Label Column -->
      <div *ngIf="label && isIcon"
           [ngClass]="'col-' + iconLabelColSize + ' justify-content-center align-items-center d-flex'"
           [matTooltip]="placeholder">
        <span class="material-symbols-rounded"><label>{{ label }}</label></span>
      </div>

      <!-- Dropdown Column -->
      <div [ngClass]="'col-' + (isIcon ? dropdownIconColSize : dropdownColSize)">
        <div class="custom-dropdown d-flex align-items-center justify-content-between"
             [matMenuTriggerFor]="menu"
             #menuTrigger
             (keydown)="handleKeydown($event)"
             (click)="setMenuWidth()"
             tabindex="0">

          <div *ngIf="selectedOptionValue as selectedOption" class="d-flex justify-content-start align-items-center value-container">
            @if (isMaporiumIcon) {
              <span [class]="selectedOption.icon"></span>
            } @else {
              <span class="material-symbols-rounded selected-icon ms-1"
                    *ngIf="selectedOption.icon">{{ selectedOption.icon }}</span>
            }
            <span class="ms-2 selected-input">{{ selectedOption.value }}</span>
          </div>

          <div *ngIf="!selectedOption && hasBlank">
            <span class="ms-2 selected-input">{{ blankValue }}</span>
          </div>

          <span class="material-symbols-rounded">arrow_drop_down</span>

          <!-- Dropdown Menu -->
          <mat-menu #menu="matMenu" class="select-input-menu">
            <ng-container *ngIf="hasBlank">
              <button mat-menu-item (click)="selectBlank()">
                <mat-icon class="empty-icon"></mat-icon>
                <span>{{ blankValue }}</span>
              </button>
              <mat-divider *ngIf="options.length > 0"></mat-divider>
            </ng-container>

            @for (option of options; track $index) {
              <button mat-menu-item
                      (click)="selectOption(option)"
                      [class.truncate]="truncate"
                      class="justify-content-start">
                <ng-container *ngIf="!noIcons">
                  @if (isMaporiumIcon) {
                    <span *ngIf="option.icon" [class]="option.icon"></span>
                  } @else {
                    <mat-icon fontSet="material-symbols-outlined"
                              *ngIf="option.icon"
                              [fontIcon]="option.icon">{{ option.icon }}
                    </mat-icon>
                  }

                  <mat-icon class="empty-icon" *ngIf="!option.icon"></mat-icon>
                </ng-container>
                <ng-container *ngIf="option.tooltipText">
                  <span
                    [matTooltip]="option.tooltipText"
                    [matTooltipPosition]="tooltipPosition">{{ option.value }}</span>
                </ng-container>
                <ng-container *ngIf="!option.tooltipText">
                  <span>{{ option.value }}</span>
                </ng-container>
              </button>
              @if (option.isSeparatorAfter) {
                <mat-divider class="mt-1 mb-2"></mat-divider>
              }
            }
          </mat-menu>
        </div>

        <select [formControl]="formControl" hidden>
          <option *ngFor="let option of options" [value]="option.key">{{ option.value }}</option>
        </select>
      </div>
    </div>
  `,
  styleUrls: ['./select-input.component.scss'],
  imports: [FormsModule, ReactiveFormsModule, CommonModule, MatTooltipModule, MatMenuModule, MatIconModule, MatListModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectInputComponent),
      multi: true,
    },
  ],
})
export class SelectInputComponent extends BaseCustomInputComponent implements OnInit, OnChanges{
  @Input() label = '';
  /**
   * Keeps an intentionally unlabeled select aligned with the labeled selects
   * around it: renders an empty column the size of the label column instead of
   * letting the dropdown slide into it.
   */
  @Input() reserveLabelSpace = false;
  @Input() placeholder = '';
  @Input() bottomMargin = false;
  @Input() value: string | number | undefined = '';
  @Input() isIcon = false;
  @Input() truncate = false;
  @Input() metadata: any;
  @Input() noIcons = false;
  @Input() hasBlank = false;
  @Input() blankValue = 'blank';
  @Input() options: {
    key: string,
    value: string,
    icon?: string,
    isSeparatorAfter?: boolean,
    tooltipText?: string
  }[] = [];
  @Input() labelColSize = 4;
  @Input() iconLabelColSize = 2;
  @Input() dropdownColSize = 8;
  @Input() dropdownIconColSize = 10;
  @Input() noMargin = false;
  @Input() isMaporiumIcon = false;
  @Input() tooltipPosition: 'left' | 'right' | 'above' | 'below' | 'before' | 'after' = 'left';

  @Output() selectionChange = new EventEmitter<{key: string, value: string, icon?: string}>();

  @ViewChild('menuTrigger') trigger: ElementRef | undefined;
  @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger;


  selectedOption: { key: string; value: string; icon?: string; } | undefined;
  isDropdownOpen = false;

  constructor(private renderer: Renderer2) {
    super();

    this.formControl.valueChanges.subscribe((value) => {
      this.selectedOption = this.options.find((option) => option.key === value);
    });
  }

  get selectedOptionValue() {
    const formValue = this.formControl.value;
    if (formValue === null || formValue === undefined) {
      return null;
    }
    return this.options.find((option) => option.key === formValue);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.metadata?.length !== undefined) {
      this.options = [
        {key: 'name', value: 'Name'},
        ...this.metadata.map((metaData: { key: any; }) => {
          return {key: metaData.key, value: metaData.key}
        })
      ];
    }
  }

  selectBlank() {
    this.selectedOption = undefined;
    this.formControl.setValue(undefined);
    this.isDropdownOpen = false;
  }

  handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === 'ArrowDown' || event.key === ' ') {
      this.menuTrigger?.openMenu();
      event.preventDefault();
    } else if (event.key === 'ArrowUp') {
      this.menuTrigger?.closeMenu();
      event.preventDefault();
    }

  }

  selectOption(option:  {key: string, value: string, icon?: string}) {
    this.selectedOption = option;
    this.formControl.setValue(option.key);
    // Reach the bound control now rather than after the base class's 200ms
    // debounce. That delay is there for typing; a pick is already final, and
    // the lag let a dialog submitted straight after a choice read the old
    // value (MAP-1682). The debounced emission that follows carries the same
    // value, so arriving twice changes nothing.
    this.onChange(option.key as never);
    this.isDropdownOpen = false;
    this.selectionChange.emit(option);
  }

  setMenuWidth() {
    if (this.trigger === undefined) {
      return;
    }
    const width = this.trigger.nativeElement.offsetWidth;
    const menuElement = document.querySelector('.mat-mdc-menu-panel');
    if (menuElement) {
      this.renderer.setStyle(menuElement, 'min-width', `${width}px`);
    }
  }
}
